import { useEffect, useMemo } from "react";
import {
  useBalanceWithPoints,
  usePointsInputValidator,
} from "@apac/components/checkout";
import type { CreditCard } from "@b2bportal/card-api";
import type { Product } from "@b2bportal/purchase-api";

interface UseSelectPaymentProps {
  savedPayments: CreditCard[];
  selectedPaymentHopperId: string | undefined;
  selectPaymentMethod: (paymentId: string | undefined) => void;
  product?: Product;
  isCardAllowed?: (card: CreditCard) => boolean;
}

export const useSelectPayment = ({
  savedPayments,
  selectedPaymentHopperId,
  selectPaymentMethod,
  product,
  isCardAllowed,
}: UseSelectPaymentProps) => {
  const selectedPayment = useMemo(
    () =>
      savedPayments.find((payment) => payment.id === selectedPaymentHopperId),
    [savedPayments, selectedPaymentHopperId]
  );

  const { isValid: isValidBalance } = usePointsInputValidator();
  const { remainingBalance } = useBalanceWithPoints(product);

  const cardPaymentDisabled = useMemo(
    () => isValidBalance && (remainingBalance?.value ?? 0) <= 0,
    [remainingBalance, isValidBalance]
  );

  // Auto-select the first ALLOWED payment method by default. If the currently
  // selected card is no longer allowed (e.g. user removed it elsewhere or the
  // tenant rules changed), clear the selection so it doesn't silently submit.
  useEffect(() => {
    const firstAllowed = isCardAllowed
      ? savedPayments?.find(isCardAllowed)
      : savedPayments?.[0];
    const selectedStillAllowed =
      !selectedPayment || !isCardAllowed || isCardAllowed(selectedPayment);

    if (cardPaymentDisabled && selectedPaymentHopperId) {
      selectPaymentMethod(undefined);
      return;
    }
    if (!selectedStillAllowed) {
      selectPaymentMethod(undefined);
      return;
    }
    if (!cardPaymentDisabled && firstAllowed && !selectedPaymentHopperId) {
      selectPaymentMethod(firstAllowed.id);
    }
  }, [
    cardPaymentDisabled,
    isCardAllowed,
    savedPayments,
    selectPaymentMethod,
    selectedPayment,
    selectedPaymentHopperId,
  ]);

  return {
    cardPaymentDisabled,
    selectedPayment,
  };
};
